import { memo, useCallback, useEffect, useMemo, useRef } from "react"
import axios from "axios"
// Common-languages build (~40 langs) instead of the full one — the full
// build is ~1MB of grammars.
import hljs from "highlight.js/lib/common"
import { useQuery } from "@tanstack/react-query"
import { useVirtualizer, type Virtualizer } from "@tanstack/react-virtual"
import {
  ContentItemCreationStatus,
  type ContentItem,
} from "@/shared/api/generated"
import {
  useMarkdownWords,
  useMarkdownWordTracker,
  useMarkdownAudioPage,
} from "@/entities/markdown-viewer"
import { activePlayer } from "@/entities/audio-engine"
import { processSocket } from "@/entities/process-socket/store"
import {
  ROUTER_PATHS,
  QUERY_KEYS,
  SOCKET_RESPONSES,
  LANG_KEYS,
} from "@/shared/constants"
import { Spinner } from "@/components/ui/spinner"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import { Undo2 } from "lucide-react"
import { useNavigate } from "react-router-dom"
import styles from "./styles.module.scss"
import { useAutoTranslation } from "@/entities"

interface MarkdownViewerProps {
  itemData: ContentItem
}

const ESTIMATED_PAGE_HEIGHT = 1300

const fetchPageHtml = async (url: string): Promise<string> => {
  const response = await axios.get<string>(url, {
    responseType: "text",
    headers: { "Cache-Control": "public, max-age=31536000" },
  })
  return response.data
}

/**
 * One virtualized page. Mounted only while inside the virtualizer's window,
 * so its HTML is fetched lazily on first approach (then cached forever by
 * react-query — remounting is instant).
 *
 * memo is load-bearing, not an optimization: React 19.0.0 re-sets
 * dangerouslySetInnerHTML on EVERY re-render (it compares the {__html}
 * object identity, not the string), which recreates all word spans and
 * wipes the imperatively-added `ready`/`active` classes. With memo the
 * subtree only re-renders when its props actually change, and the effect
 * below re-marks right after.
 */
const MarkdownPage = memo(function MarkdownPage({
  contentId,
  url,
  index,
  readyWordIdxs,
  measureElement,
}: {
  contentId: string
  url: string
  index: number
  /** wordIdx values on this page that already have aligned timings */
  readyWordIdxs?: Set<number>
  measureElement: Virtualizer<HTMLDivElement, Element>["measureElement"]
}) {
  const { data } = useQuery({
    queryKey: QUERY_KEYS.MARKDOWN_PAGE(contentId, index),
    queryFn: () => fetchPageHtml(url),
    staleTime: Infinity,
  })

  const bodyRef = useRef<HTMLDivElement>(null)

  // Syntax-highlight fenced code blocks (server renders them as plain
  // escaped text in <pre><code class="language-x">). Safe to let hljs
  // rewrite their innerHTML: code content is never word-wrapped, so there
  // are no tts-word spans inside. Depends on readyWordIdxs too because any
  // prop-driven re-render re-sets dangerouslySetInnerHTML (see memo note
  // above), replacing the highlighted nodes with fresh unhighlighted ones.
  useEffect(() => {
    if (data === undefined) return
    bodyRef.current
      ?.querySelectorAll<HTMLElement>('pre code[class*="language-"]')
      .forEach(block => hljs.highlightElement(block))
  }, [data, readyWordIdxs])

  // Paint the subtle "this word is ready" overlay. Imperative because the
  // spans live inside dangerouslySetInnerHTML content; re-runs both when new
  // chunks land (readyWordIdxs grows) and when the page's HTML (re)mounts
  // under virtualization (fresh spans without the class).
  useEffect(() => {
    if (data === undefined || !readyWordIdxs) return
    for (const wordIdx of readyWordIdxs) {
      document.getElementById(`w${wordIdx}`)?.classList.add("ready")
    }
  }, [data, readyWordIdxs])

  return (
    // Vertical padding is load-bearing, not just visual: it stops the first
    // heading's margin-top from collapsing out of this measured element —
    // collapsed-out margins escape the measured height and make the next
    // page overlap this one.
    <div
      ref={measureElement}
      data-index={index}
      className="py-3 sm:py-4 lg:py-6"
    >
      <div className="mx-auto w-full max-w-[1100px] px-3 sm:px-5 md:px-8 lg:px-10 xl:px-12">
        {data !== undefined ? (
          <div
            ref={bodyRef}
            className={styles.markdownBody}
            dangerouslySetInnerHTML={{ __html: data }}
          />
        ) : (
          <div className="space-y-3 py-4">
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        )}
      </div>
    </div>
  )
})

export const MarkdownViewer = ({ itemData }: MarkdownViewerProps) => {
  const { t } = useAutoTranslation()
  const navigate = useNavigate()
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  const totalPages = itemData.markdownPageUrls.length

  const isProcessing =
    itemData.creationStatus ===
    ContentItemCreationStatus.PARTIALLY_COMPLETED_AND_STILL_PROCESSING

  const combinedUrls = isProcessing
    ? undefined
    : itemData.combinedDataAbsoluteUrls

  // ─── Virtualized pages (heights measured lazily) ────────

  const virtualizer = useVirtualizer({
    count: totalPages,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ESTIMATED_PAGE_HEIGHT,
    overscan: 2,
  })

  const virtualItems = virtualizer.getVirtualItems()

  // Scroll-visible page (center of the rendered window) — drives which
  // pages' word timings are kept loaded.
  const scrollPage = useMemo(() => {
    if (!virtualItems.length) return -1
    return virtualItems[Math.floor(virtualItems.length / 2)].index
  }, [virtualItems])

  // ─── Audio position → page ──────────────────────────────

  const { audioPage, setAudioPage, searchForPosition } = useMarkdownAudioPage(
    itemData.id,
    totalPages,
    combinedUrls
  )

  // ─── Word timings + active-word highlight ───────────────

  const { words, wordByIdx, invalidate } = useMarkdownWords(
    itemData.id,
    audioPage,
    scrollPage,
    combinedUrls
  )

  // Per-page sets of aligned wordIdx values — drives the subtle "ready"
  // overlay. Applied in both modes so the visual language stays consistent:
  // during processing it shows chunk progress, on completed content every
  // aligned (= clickable) word carries it, same as the PDF viewer.
  const readyWordIdxsByPage = useMemo(() => {
    const byPage = new Map<number, Set<number>>()
    for (const word of words) {
      if (word.wordIdx === null || word.wordIdx === undefined) continue
      let set = byPage.get(word.pageIndex)
      if (!set) {
        set = new Set()
        byPage.set(word.pageIndex, set)
      }
      set.add(word.wordIdx)
    }
    return byPage
  }, [words])

  const handleMiss = useCallback(
    (position: number) => {
      if (combinedUrls?.length) {
        // Seek landed outside the loaded window — locate its page
        searchForPosition(position)
      } else {
        // Processing: alignment for this position may have landed by now
        invalidate()
      }
    },
    [combinedUrls, searchForPosition, invalidate]
  )

  const scrollToPage = useCallback(
    (pageIndex: number) => {
      virtualizer.scrollToIndex(pageIndex, { align: "start" })
    },
    [virtualizer]
  )

  const { activeWord, autoScroll, setAutoScroll } = useMarkdownWordTracker(
    words,
    scrollContainerRef,
    setAudioPage,
    handleMiss,
    scrollToPage
  )

  // Big seek / initial load: word not rendered yet — jump to the audio page
  // placeholder; once its HTML and words arrive the tracker fine-scrolls to
  // the exact word. Mirror of the PDF viewer's "scroll to page when no word".
  useEffect(() => {
    if (!autoScroll || audioPage < 0 || activeWord) return
    virtualizer.scrollToIndex(audioPage, { align: "start" })
  }, [autoScroll, audioPage, activeWord, virtualizer])

  // New aligned chunks land while listening to still-processing content.
  // Audio-first chunks announce their words separately (CHUNK_WORDS), so
  // listen to both: the chunk event alone may arrive before any words exist.
  useEffect(() => {
    if (!isProcessing) return
    const handler = () => invalidate()
    processSocket.on(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
    processSocket.on(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    return () => {
      processSocket.off(SOCKET_RESPONSES.CONTENT_NEW_CHUNK_COMPLETED, handler)
      processSocket.off(SOCKET_RESPONSES.CONTENT_CHUNK_WORDS_COMPLETED, handler)
    }
  }, [isProcessing, invalidate])

  // ─── Audio ──────────────────────────────────────────────

  // On mount: play if user navigated within app, or just load (no play) on
  // browser reload — same behavior as PdfViewer.
  const hasStartedAudio = useRef(false)
  useEffect(() => {
    if (hasStartedAudio.current) return
    hasStartedAudio.current = true
    if (activePlayer.id === itemData.id && activePlayer.isActive) return
    if (navigator.userActivation?.hasBeenActive) {
      activePlayer.play(itemData)
    } else {
      activePlayer.load(itemData)
    }
  }, [itemData])

  // Click any word span → seek there. Delegated: spans live inside
  // dangerouslySetInnerHTML content, so React can't attach per-span handlers.
  const handleContentClick = useCallback(
    (e: React.MouseEvent) => {
      const span = (e.target as HTMLElement).closest?.(".tts-word")
      if (!span) return
      const match = /^w(\d+)$/.exec(span.id)
      if (!match) return
      const word = wordByIdx.get(Number(match[1]))
      if (!word) return
      activePlayer.seekToAndPlay(word.start_ts)
    },
    [wordByIdx]
  )

  if (!totalPages) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner />
      </div>
    )
  }

  return (
    <div className="flex h-full w-full flex-col">
      {/* Top Navigation Bar */}
      <div
        className="bg-background flex items-center justify-between border-b px-2"
        style={{ minHeight: "56px" }}
      >
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(ROUTER_PATHS.APP_LIBRARY)}
        >
          <Undo2 className="h-5 w-5" />
        </Button>

        {/* Right: Auto Scroll */}
        <div className="flex items-center gap-1.5">
          <Checkbox
            id="auto-scroll"
            checked={autoScroll}
            onCheckedChange={checked => setAutoScroll(checked === true)}
          />
          <Label
            htmlFor="auto-scroll"
            className="cursor-pointer text-sm font-medium"
          >
            {t(LANG_KEYS.AUTO_SCROLL)}
          </Label>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto" ref={scrollContainerRef}>
        <div
          className="relative w-full"
          style={{ height: `${virtualizer.getTotalSize()}px` }}
          onClick={handleContentClick}
        >
          {virtualItems.map(item => (
            <div
              key={`md-page-${itemData.id}-${item.index}`}
              className="absolute top-0 left-0 w-full"
              style={{ transform: `translateY(${item.start}px)` }}
            >
              <MarkdownPage
                contentId={itemData.id}
                url={itemData.markdownPageUrls[item.index]}
                index={item.index}
                readyWordIdxs={readyWordIdxsByPage.get(item.index)}
                measureElement={virtualizer.measureElement}
              />
            </div>
          ))}
        </div>
        <div className="h-24" />
      </div>
    </div>
  )
}
